import type { FormEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaCheckCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCloudSun,
  FaPlus,
  FaExternalLinkAlt,
  FaChevronDown,
  FaTimes,
  FaClipboardList,
} from "react-icons/fa";
import type { GuestBalanceHint } from "@/lib/v2/guestBalance";
import type { LineupMember } from "@/lib/v2/lineup";
import { HomeLineupPanel, HomeLineupPanelRoot, HomeLineupMemberView, HomeLineupAdminControls, useHomeLineupStickyPrimary, type LineupPlayer } from "@/components/v2/home/HomeLineupPanel";
import type { TeamPlayerVM } from "@/components/v2/home/HomeConfirmedCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HomePhaseShell, HomePhaseBadge } from "@/components/v2/home/HomePhaseShell";
import { homeFieldInteractive, homeInputField } from "@/components/v2/home/homeFieldStyles";
import { HomeSecondarySection } from "@/components/v2/home/HomeSecondarySection";
import { HomeConfirmationProgress } from "@/components/v2/home/HomeConfirmationProgress";
import { HomeAdminZone } from "@/components/v2/home/HomeAdminZone";
import { HomeMobileAdminStickyBar, type HomeMobileStickyAction } from "@/components/v2/home/HomeMobileAdminStickyBar";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn, destructiveOutlineBtnClass } from "@/lib/utils";

type RsvpStatus = "going" | "maybe" | "out" | "reserve";

export interface RosterEntry {
  id: string;
  name: string;
  playerId?: number | null;
  guestName?: string | null;
  status: RsvpStatus;
}

const RSVP_STATUS_OPTIONS: RsvpStatus[] = ["going", "maybe", "out", "reserve"];

interface HomeCollectingCardProps {
  isAdmin?: boolean;
  canManageRsvp?: boolean;
  canAddGuest?: boolean;
  weatherSummary?: string | null;
  relativeDate: string;
  location: string | null;
  maxPlayers: number;
  rsvpStatus: RsvpStatus | null;
  onRsvp: (status: "going" | "maybe" | "out") => void;
  goingCount: number;
  minPlayers: number;
  confirmed: boolean;
  minConfirmPct: number;
  unrespondedPlayers?: { id: number; name: string }[];
  onAddAttendance?: (
    target: { guestName?: string; playerId?: number; guestBalanceHint?: GuestBalanceHint },
    status: RsvpStatus
  ) => Promise<void>;
  going: RosterEntry[];
  maybe: RosterEntry[];
  out: RosterEntry[];
  reserve: RosterEntry[];
  onAdminRsvpChange?: (entry: RosterEntry, status: RsvpStatus) => void;
  changingRsvpId?: string | null;
  hasPublishedLineup?: boolean;
  teamA?: TeamPlayerVM[];
  teamB?: TeamPlayerVM[];
  currentUserTeam?: "A" | "B" | null;
  eventId?: string;
  goingPlayers?: LineupPlayer[];
  currentUserPlayerId?: number | null;
  publishingLineup?: boolean;
  onPublishLineup?: (teamA: LineupMember[], teamB: LineupMember[]) => Promise<void>;
  onShareWhatsApp?: () => void;
  onCancelMatch?: () => void;
  cancellingMatch?: boolean;
  onEnterResult?: () => void;
  enteringResult?: boolean;
  headerAction?: ReactNode;
}

function mapsUrl(location: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    location
  )}`;
}

export function HomeCollectingCard({
  isAdmin = false,
  canManageRsvp = false,
  canAddGuest = false,
  weatherSummary,
  relativeDate,
  location,
  maxPlayers,
  rsvpStatus,
  onRsvp,
  goingCount,
  minPlayers,
  confirmed,
  minConfirmPct,
  unrespondedPlayers = [],
  onAddAttendance,
  going,
  maybe,
  out,
  reserve,
  onAdminRsvpChange,
  changingRsvpId = null,
  hasPublishedLineup = false,
  teamA = [],
  teamB = [],
  currentUserTeam = null,
  eventId,
  goingPlayers = [],
  currentUserPlayerId,
  publishingLineup = false,
  onPublishLineup,
  onShareWhatsApp,
  onCancelMatch,
  cancellingMatch = false,
  onEnterResult,
  enteringResult = false,
  headerAction,
}: HomeCollectingCardProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [guestName, setGuestName] = useState("");
  const [guestHint, setGuestHint] = useState<GuestBalanceHint>("unknown");
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [addStatus, setAddStatus] = useState<RsvpStatus>("going");
  const [addingGuest, setAddingGuest] = useState(false);
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [adminSectionOpen, setAdminSectionOpen] = useState(false);

  const statusLabel = (status: RsvpStatus) => {
    if (status === "reserve") return t("v2.home.reserveStatus");
    return t(`v2.home.${status}`);
  };

  const handleAddGuestSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!onAddAttendance || addingGuest) return;

    const name = guestName.trim();
    if (!name) return;

    try {
      setAddingGuest(true);
      await onAddAttendance({ guestName: name, guestBalanceHint: guestHint }, "going");
      setGuestName("");
      setGuestHint("unknown");
    } finally {
      setAddingGuest(false);
    }
  };

  const handleAddPlayerSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!onAddAttendance || addingPlayer || !selectedPlayerId) return;

    try {
      setAddingPlayer(true);
      await onAddAttendance({ playerId: Number(selectedPlayerId) }, addStatus);
      setSelectedPlayerId("");
      setAddStatus("going");
    } finally {
      setAddingPlayer(false);
    }
  };

  const renderRosterRow = (entry: RosterEntry, withCheck = false) => {
    const isChanging = changingRsvpId === entry.id;
    const muted = !withCheck;

    if (canManageRsvp && onAdminRsvpChange) {
      return (
        <DropdownMenu key={entry.id}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={isChanging}
              className={`flex w-full min-h-[44px] items-center gap-2 rounded-md px-1 py-1 -mx-1 text-left transition-colors hover:bg-accent/50 disabled:opacity-50 ${
                muted ? "text-muted-foreground" : ""
              }`}
            >
              {withCheck && (
                <FaCheckCircle className="text-soccer-primary text-xs shrink-0" />
              )}
              <span className="flex-1 truncate">{entry.name}</span>
              <FaChevronDown className="text-[10px] opacity-50 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>{t("v2.home.changeAttendance")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {RSVP_STATUS_OPTIONS.map((status) => (
              <DropdownMenuItem
                key={status}
                disabled={status === entry.status || isChanging}
                onClick={() => onAdminRsvpChange(entry, status)}
              >
                {status === entry.status ? (
                  <FaCheck className="mr-2 h-3 w-3" />
                ) : (
                  <span className="mr-2 inline-block w-3" />
                )}
                {statusLabel(status)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <div
        key={entry.id}
        className={`flex items-center gap-1.5 min-w-0 ${muted ? "text-muted-foreground" : ""}`}
      >
        {withCheck && <FaCheckCircle className="text-soccer-primary text-xs shrink-0" />}
        <span className="truncate">{entry.name}</span>
      </div>
    );
  };

  const renderColumn = (
    title: string,
    titleClass: string,
    entries: RosterEntry[],
    withCheck = false
  ) => (
    <div className="min-w-0 space-y-1">
      <div className={`font-semibold text-xs ${titleClass}`}>{title}</div>
      {entries.length === 0 ? (
        <div className="text-muted-foreground italic">—</div>
      ) : (
        entries.map((entry) => renderRosterRow(entry, withCheck))
      )}
    </div>
  );

  const guestAddForm =
    canAddGuest && onAddAttendance ? (
      <form onSubmit={handleAddGuestSubmit} className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1 basis-[160px]">
          <Label htmlFor="add-guest" className="sr-only">
            {t("v2.home.addGuest")}
          </Label>
          <Input
            id="add-guest"
            placeholder={t("v2.home.guestPlaceholder")}
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            className={cn(homeInputField, "min-h-[44px]")}
          />
        </div>
        <div className="w-full sm:w-auto sm:shrink-0">
          <Label htmlFor="add-guest-hint" className="sr-only">
            {t("v2.home.guestHintLabel")}
          </Label>
          <Select
            value={guestHint}
            onValueChange={(value) => setGuestHint(value as GuestBalanceHint)}
          >
            <SelectTrigger id="add-guest-hint" className={cn(homeInputField, "min-h-[44px] w-full sm:w-[150px]")}>
              <SelectValue placeholder={t("v2.home.guestHintLabel")} />
            </SelectTrigger>
            <SelectContent>
              {(
                [
                  ["unknown", t("v2.home.guestHintUnknown")],
                  ["regular", t("v2.home.guestHintRegular")],
                  ["strong", t("v2.home.guestHintStrong")],
                ] as const
              ).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="submit"
          size="icon"
          disabled={!guestName.trim() || addingGuest}
          className="bg-soccer-primary text-black hover:bg-soccer-primary/90 min-h-[44px] min-w-[44px] shrink-0"
          aria-label={t("v2.home.addGuest")}
        >
          <FaPlus />
        </Button>
      </form>
    ) : null;

  const adminPlayerAddForm =
    canManageRsvp && onAddAttendance && unrespondedPlayers.length > 0 ? (
      <form onSubmit={handleAddPlayerSubmit} className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[160px]">
          <Label htmlFor="add-player" className="sr-only">
            {t("v2.home.addPlayer")}
          </Label>
          <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
            <SelectTrigger id="add-player" className={cn(homeInputField, "min-h-[44px]")}>
              <SelectValue placeholder={t("v2.home.selectPlayerToManage")} />
            </SelectTrigger>
            <SelectContent>
              {unrespondedPlayers.map((player) => (
                <SelectItem key={player.id} value={String(player.id)}>
                  {player.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-auto">
          <Label htmlFor="add-status" className="sr-only">
            {t("v2.home.changeAttendance")}
          </Label>
          <Select
            value={addStatus}
            onValueChange={(value) => setAddStatus(value as RsvpStatus)}
          >
            <SelectTrigger id="add-status" className={cn(homeInputField, "min-h-[44px] w-full sm:w-[130px]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RSVP_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {statusLabel(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="submit"
          size="icon"
          disabled={!selectedPlayerId || addingPlayer}
          className="bg-soccer-primary text-black hover:bg-soccer-primary/90 min-h-[44px] min-w-[44px] shrink-0"
          aria-label={t("v2.home.addPlayer")}
        >
          <FaPlus />
        </Button>
      </form>
    ) : null;

  const meta = (
    <>
      <span className="flex items-center gap-1.5">
        <FaCalendarAlt className="text-soccer-primary shrink-0" />
        {relativeDate || t("v2.home.eventPending")}
      </span>
      <span className="flex items-center gap-1.5">
        <FaMapMarkerAlt className="text-soccer-primary shrink-0" />
        {location ? (
          <a
            href={mapsUrl(location)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-soccer-primary underline-offset-2 hover:underline"
          >
            {location}
            <FaExternalLinkAlt className="text-[10px]" />
            <span className="sr-only">{t("v2.home.viewOnMap")}</span>
          </a>
        ) : (
          t("v2.home.locationTbd")
        )}
      </span>
      <span className="flex items-center gap-1.5">
        <FaCloudSun className="text-soccer-primary shrink-0" />
        {weatherSummary ?? t("v2.home.weatherPlaceholder")}
      </span>
    </>
  );

  const heroNote = (
    <span className={confirmed ? "text-soccer-primary" : "text-amber-600"}>
      {confirmed ? t("v2.home.quorumConfirmedFlash") : t("v2.home.quorumWaiting")}
    </span>
  );

  const adminSectionContent =
    canManageRsvp || (isAdmin && onCancelMatch) ? (
      <div className="space-y-4">
        {canManageRsvp && adminPlayerAddForm && (
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-900/80 dark:text-amber-200/90">
              {t("v2.home.adminZoneRsvpTitle")}
            </p>
            {adminPlayerAddForm}
          </div>
        )}
        {isAdmin && onCancelMatch && (
          <Button
            type="button"
            variant="outline"
            className={cn("w-full min-h-[44px] font-semibold", destructiveOutlineBtnClass)}
            disabled={cancellingMatch}
            onClick={onCancelMatch}
          >
            <FaTimes className="mr-1.5 opacity-70" />
            {cancellingMatch ? t("common.loading") : t("v2.home.cancelMatch")}
          </Button>
        )}
      </div>
    ) : undefined;

  const showLineup = Boolean(
    eventId && (isAdmin || confirmed || hasPublishedLineup)
  );
  const mergeAdminLineup = isAdmin && showLineup && Boolean(onPublishLineup);
  const hasAdminSection =
    Boolean(adminSectionContent) || mergeAdminLineup;

  const lineupProps = {
    isAdmin,
    hasPublishedLineup: Boolean(hasPublishedLineup),
    publishedTeamA: teamA,
    publishedTeamB: teamB,
    currentUserTeam,
    goingPlayers,
    eventId: eventId ?? "",
    currentUserPlayerId,
    publishingLineup,
    onPublishLineup,
    onShareWhatsApp,
    mergedAdminSection: mergeAdminLineup,
  };

  const mergedAdminSectionContent = hasAdminSection ? (
    <div className="space-y-4">
      {canManageRsvp && adminPlayerAddForm && (
        <div className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-900/80 dark:text-amber-200/90">
            {t("v2.home.adminZoneRsvpTitle")}
          </p>
          {adminPlayerAddForm}
        </div>
      )}
      {mergeAdminLineup && (
        <div
          className={cn(
            canManageRsvp && adminPlayerAddForm && "border-t border-amber-500/20 pt-4"
          )}
        >
          <HomeLineupAdminControls />
        </div>
      )}
      {mergeAdminLineup && hasPublishedLineup && onEnterResult && (
        <Button
          type="button"
          onClick={onEnterResult}
          disabled={enteringResult}
          className="w-full min-h-[48px] bg-soccer-primary text-black hover:bg-soccer-primary/90 font-bold shadow-sm"
        >
          <FaClipboardList className="mr-1.5" />
          {enteringResult ? t("common.loading") : t("v2.home.enterResult")}
        </Button>
      )}
      {isAdmin && onCancelMatch && (
        <Button
          type="button"
          variant="outline"
          className={cn("w-full min-h-[44px] font-semibold", destructiveOutlineBtnClass)}
          disabled={cancellingMatch}
          onClick={onCancelMatch}
        >
          <FaTimes className="mr-1.5 opacity-70" />
          {cancellingMatch ? t("common.loading") : t("v2.home.cancelMatch")}
        </Button>
      )}
    </div>
  ) : undefined;

  const adminZoneConfig = hasAdminSection
    ? {
        title: mergeAdminLineup
          ? t("v2.home.adminZoneConvocatoriaTitle")
          : t("v2.home.adminZoneRsvpTitle"),
        description: mergeAdminLineup
          ? t("v2.home.adminZoneConvocatoriaDesc")
          : canManageRsvp
            ? t("v2.home.adminZoneRsvpDesc")
            : undefined,
        actionRequired:
          (canManageRsvp && !confirmed) ||
          (mergeAdminLineup && !hasPublishedLineup),
      }
    : undefined;

  const adminPanelBody = mergeAdminLineup ? mergedAdminSectionContent : adminSectionContent;
  const adminPanelWrapped =
    adminPanelBody && adminZoneConfig ? (
      <HomeAdminZone {...adminZoneConfig}>{adminPanelBody}</HomeAdminZone>
    ) : (
      adminPanelBody
    );

  const mobileAdminSecondary =
    isMobile && hasAdminSection && adminPanelWrapped ? (
      <HomeSecondarySection
        label={adminZoneConfig?.title ?? t("v2.home.adminZoneTitle")}
        summary={
          adminZoneConfig?.actionRequired
            ? t("v2.home.adminActionRequired")
            : undefined
        }
        defaultOpen={false}
        onOpenChange={setAdminSectionOpen}
      >
        {adminPanelWrapped}
      </HomeSecondarySection>
    ) : null;

  const showStickyBar =
    isMobile &&
    mergeAdminLineup &&
    hasAdminSection &&
    (adminSectionOpen || Boolean(adminZoneConfig?.actionRequired));

  const collectingShell = (
    <HomePhaseShell
      eyebrow={t("v2.home.phaseEyebrowConvocatoria")}
      title={confirmed ? t("v2.home.confirmedTitle") : t("v2.home.nextMatch")}
      eyebrowBadge={
        <HomePhaseBadge tone={confirmed ? "primary" : "amber"}>
          {confirmed ? t("v2.home.confirmedBadge") : t("v2.home.convocatoriaBadge")}
        </HomePhaseBadge>
      }
      meta={meta}
      heroNote={heroNote}
      headerAction={headerAction}
      accent={confirmed ? "success" : "default"}
      primaryAction={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onRsvp("going")}
            className={cn(
              "flex flex-1 min-h-[48px] items-center justify-center gap-1.5 rounded-lg border font-semibold",
              rsvpStatus === "going"
                ? "border-soccer-primary bg-soccer-primary text-black"
                : homeFieldInteractive
            )}
          >
            <FaCheckCircle className="shrink-0" />
            {t("v2.home.going")}
          </button>
          <button
            type="button"
            onClick={() => onRsvp("out")}
            className={cn(
              "flex flex-1 min-h-[48px] items-center justify-center rounded-lg border font-semibold",
              rsvpStatus === "out"
                ? "border-destructive bg-destructive text-destructive-foreground"
                : homeFieldInteractive
            )}
          >
            {t("v2.home.out")}
          </button>
          <button
            type="button"
            onClick={() => onRsvp("maybe")}
            className={cn(
              "flex flex-1 min-h-[48px] items-center justify-center rounded-lg border font-semibold",
              rsvpStatus === "maybe"
                ? "border-amber-500 bg-amber-500/15 text-amber-900 dark:text-amber-200"
                : homeFieldInteractive
            )}
          >
            {t("v2.home.maybe")}
          </button>
        </div>
      }
      secondary={
        <div className="space-y-4">
          <HomeSecondarySection
            label={t("v2.home.rosterTitle")}
            summary={t("v2.home.rosterCount", { count: going.length })}
          >
            <div className="space-y-3">
              {guestAddForm && (
                <div className="space-y-2 rounded-lg border border-dashed border-border/70 bg-muted/20 p-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("v2.home.addGuestSection")}
                  </p>
                  {guestAddForm}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
                {renderColumn(t("v2.home.goingList"), "text-soccer-primary", going, true)}
                {renderColumn(t("v2.home.maybeList"), "text-amber-500", maybe)}
                {renderColumn(t("v2.home.outList"), "text-destructive", out)}
                {renderColumn(t("v2.home.reserveList"), "text-blue-500", reserve)}
              </div>
            </div>
          </HomeSecondarySection>

          {showLineup && (
            <div className="border-t border-dashed pt-4">
              {mergeAdminLineup ? (
                <HomeLineupMemberView />
              ) : (
                <HomeLineupPanel
                  isAdmin={isAdmin}
                  hasPublishedLineup={Boolean(hasPublishedLineup)}
                  publishedTeamA={teamA}
                  publishedTeamB={teamB}
                  currentUserTeam={currentUserTeam}
                  goingPlayers={goingPlayers}
                  eventId={eventId!}
                  currentUserPlayerId={currentUserPlayerId}
                  publishingLineup={publishingLineup}
                  onPublishLineup={onPublishLineup}
                  onShareWhatsApp={onShareWhatsApp}
                />
              )}
            </div>
          )}

          {mobileAdminSecondary}
        </div>
      }
      adminSection={isMobile ? undefined : adminPanelBody}
      adminZone={isMobile ? undefined : adminZoneConfig}
    >
      <HomeConfirmationProgress
        goingCount={goingCount}
        minPlayers={minPlayers}
        maxPlayers={maxPlayers}
        confirmed={confirmed}
        minConfirmPct={minConfirmPct}
      />
      <p className="text-[11px] text-muted-foreground">{t("v2.home.maybeDoesNotCount")}</p>
    </HomePhaseShell>
  );

  if (mergeAdminLineup) {
    return (
      <HomeLineupPanelRoot {...lineupProps}>
        <div className={cn(showStickyBar && "pb-14")}>{collectingShell}</div>
        <ConvocatoriaStickyBar
          show={showStickyBar}
          hasPublishedLineup={hasPublishedLineup}
          onEnterResult={onEnterResult}
          enteringResult={enteringResult}
        />
      </HomeLineupPanelRoot>
    );
  }

  return collectingShell;
}

function ConvocatoriaStickyBar({
  show,
  hasPublishedLineup,
  onEnterResult,
  enteringResult,
}: {
  show: boolean;
  hasPublishedLineup: boolean;
  onEnterResult?: () => void;
  enteringResult?: boolean;
}) {
  const { t } = useTranslation();
  const lineupSticky = useHomeLineupStickyPrimary();

  const actions = useMemo((): HomeMobileStickyAction[] => {
    const list: HomeMobileStickyAction[] = [];
    if (lineupSticky) {
      list.push({
        key: "lineup",
        label: lineupSticky.loading ? t("common.loading") : lineupSticky.label,
        onClick: lineupSticky.onClick,
        disabled: lineupSticky.disabled || lineupSticky.loading,
        variant: "primary",
      });
    }
    if (hasPublishedLineup && onEnterResult) {
      list.push({
        key: "result",
        label: enteringResult ? t("common.loading") : t("v2.home.enterResult"),
        onClick: onEnterResult,
        disabled: enteringResult,
        variant: lineupSticky ? "outline" : "primary",
      });
    }
    return list;
  }, [lineupSticky, hasPublishedLineup, onEnterResult, enteringResult, t]);

  if (!show || actions.length === 0) return null;
  return <HomeMobileAdminStickyBar actions={actions} />;
}
